import React, { useMemo } from "react";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import { FaHeart, FaRegHeart } from "react-icons/fa";
import { CatalogImageOwner } from "../../../features/floorplan3d/catalog/catalogQueries";
import {
  useToggleProductFavorite,
  useUserFavoriteProducts,
} from "../../../gql/userFavorites";

type Props = {
  product: CatalogImageOwner;
};

/** Client-matched heart: gray outline / filled gray (not red). */
export const favoriteHeartClassName =
  "absolute right-2 top-2 z-10 flex h-8 w-8 items-center justify-center rounded-full bg-white/90 text-gray-700 shadow-sm backdrop-blur-sm transition hover:bg-white hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#111] disabled:opacity-60";

/**
 * Heart toggle for catalog products — same account favorites as the moodboard editor.
 * Hidden until favorites have loaded so we never flash an empty outline as "not liked".
 */
export const ProductFavoriteButton: React.FC<Props> = observer(({ product }) => {
  const { t } = useTranslation();
  const { data, loading: favoritesLoading } = useUserFavoriteProducts();
  const { toggle, loading: toggleLoading } = useToggleProductFavorite();
  const isFavorite = useMemo(
    () =>
      Boolean(data?.userFavoriteProducts?.some((item) => item.id === product.id)),
    [data?.userFavoriteProducts, product.id],
  );

  // Match client FavoriteButton: wait for favorites before showing the heart.
  if (favoritesLoading && !data?.userFavoriteProducts) {
    return null;
  }
  if (!data?.userFavoriteProducts) {
    return null;
  }

  const label = isFavorite
    ? t("favorites.removeFromFavorites")
    : t("favorites.addToFavorites");

  return (
    <button
      type="button"
      aria-label={label}
      aria-pressed={isFavorite}
      title={label}
      disabled={toggleLoading}
      onClick={(event) => {
        event.stopPropagation();
        event.preventDefault();
        void toggle(product, isFavorite);
      }}
      className={`${favoriteHeartClassName} ${toggleLoading ? "animate-pulse" : ""}`}
    >
      {isFavorite ? (
        <FaHeart size={14} aria-hidden />
      ) : (
        <FaRegHeart size={14} aria-hidden />
      )}
    </button>
  );
});
